import type { FloorDef } from '../types';
import { room, door, stairs } from '../dsl';

/**
 * 2층 — 현관홀 위의 계단참(landing)에서 서쪽 복도가 뻗고, 잠긴 동쪽 날개 문 너머로 주인 침실·회랑·3층 계단이 이어진다.
 * 서쪽(자유): 손님방·욕실·육아실·교실·재봉실·음악 연습실. 동쪽(key_east): 주인 침실(금고)·의상실(감옥 후보)·초상화 회랑·3층 계단·하인 계단.
 * Grid 128 x 80. 복도 y=45..48, 계단참 x=52..75 y=50..61 (1층 현관홀 바로 위).
 */
export const SECOND: FloorDef = {
  id: 'second',
  w: 128,
  h: 80,
  rooms: [
    // ---- 중심: 계단참 + 복도 ----
    room('landing', '2층 계단참', 52, 50, 24, 12, 'stairhall', 'carpetRed', 'cream', {
      lights: [{ x: 64 * 16, y: 56 * 16, r: 60, color: '#5a6fb0', id: 'landing-moon' }],
    }),
    room('wcorr2', '2층 서쪽 복도', 30, 45, 46, 4, 'corridor', 'carpetRed', 'green'),
    room('ecorr2', '2층 동쪽 복도', 77, 45, 46, 4, 'corridor', 'carpetBlue', 'blue'),

    // ---- 서쪽 날개 (자유 구역) ----
    // 복도 북쪽: 손님방들, 린넨실, 욕실 (북쪽 벽이 바깥이라 창문)
    room('guest1', '손님방 1', 30, 33, 10, 11, 'guest', 'wood', 'cream', { windows: 0.5 }),
    room('guest2', '손님방 2', 41, 33, 10, 11, 'guest', 'wood', 'blue', { windows: 0.5 }),
    room('linen', '린넨실', 52, 37, 5, 7, 'linen', 'plank', 'plaster'),
    room('bath2', '욕실', 58, 36, 7, 8, 'bath', 'checker', 'plaster', { windows: 0.3 }),
    room('guest3', '손님방 3', 66, 33, 10, 11, 'guest', 'wood', 'green', { windows: 0.5 }),
    // 복도 서쪽 끝: 발코니
    room('wbalcony', '서쪽 발코니', 20, 44, 9, 6, 'balcony', 'stone', 'stone', { windows: 1 }),
    // 복도 남쪽, 계단참 서쪽: 육아실·교실, 그 아래 재봉실
    room('nursery', '육아실', 30, 50, 11, 12, 'nursery', 'carpetBlue', 'cream'),
    room('schoolroom', '교실', 42, 50, 9, 12, 'schoolroom', 'wood', 'green'),
    room('sewing', '재봉실', 42, 63, 9, 10, 'sewing', 'wood', 'cream'),
    // 계단참 남쪽: 음악 연습실(서), 트렁크 보관실(동) — 사이에 대계단이 남쪽 벽에 붙어 있다
    room('practice', '음악 연습실', 52, 63, 9, 12, 'music', 'wood', 'blue', { windows: 0 }),
    room('trunkroom', '트렁크 보관실', 67, 63, 9, 10, 'storage', 'plank', 'plaster'),

    // ---- 동쪽 날개 (key_east 뒤) ----
    // 복도 북쪽: 초상화 회랑(넓게), 주인 서재, 동쪽 손님방
    room('gallery2', '초상화 회랑', 77, 35, 24, 9, 'gallery', 'carpetBlue', 'blue', {
      decor: [
        { kind: 'painting', x: 81, y: 34, id: 'gal-p1', data: { slot: 0 } },
        { kind: 'painting', x: 86, y: 34, id: 'gal-p2', data: { slot: 1 } },
        { kind: 'painting', x: 91, y: 34, id: 'gal-p3', data: { slot: 2 } },
        { kind: 'painting', x: 96, y: 34, id: 'gal-p4', data: { slot: 3 } },
      ],
    }),
    room('study2', '주인 서재', 102, 33, 10, 11, 'study', 'wood', 'panel', { windows: 0.5 }),
    room('guest4', '동쪽 손님방', 113, 33, 10, 11, 'guest', 'wood', 'green', { windows: 0.5 }),
    // 복도 동쪽 끝: 발코니
    room('ebalcony', '동쪽 발코니', 124, 44, 3, 6, 'balcony', 'stone', 'stone', { windows: 1 }),
    // 복도 남쪽: 주인 침실(금고) → 의상실(감옥), 3층 계단실, 하인 계단실 → 위층 하인 계단(판자문)
    room('master', '주인 침실', 77, 50, 18, 14, 'master', 'carpetRed', 'panel', {
      props: [{ kind: 'safe', id: 'master-safe', x: 91, y: 50, data: { code: 'master' } }],
    }),
    room('dressing', '의상실', 96, 50, 9, 12, 'dressing', 'wood', 'cream', { prison: true }),
    room('thirdstair', '3층 계단실', 106, 50, 7, 9, 'stairhall', 'plank', 'plaster'),
    room('servstair2', '하인 계단실', 114, 50, 9, 9, 'stairhall', 'plank', 'plaster'),
    room('servup2', '위층 하인 계단', 114, 60, 9, 8, 'stairhall', 'plank', 'plaster'),
  ],
  doors: [
    // 중심
    door('landing', 'wcorr2', 'arch'),
    door('wcorr2', 'ecorr2', 'locked', { id: 'east-door', key: 'key_east', label: '동쪽 날개 문' }),
    // 서쪽 날개
    door('wcorr2', 'wbalcony', 'door'),
    door('wcorr2', 'guest1', 'door'),
    door('wcorr2', 'guest2', 'door'),
    door('wcorr2', 'linen', 'door'),
    door('wcorr2', 'bath2', 'door'),
    door('wcorr2', 'guest3', 'door'),
    door('wcorr2', 'nursery', 'door'),
    door('wcorr2', 'schoolroom', 'door'),
    door('nursery', 'schoolroom', 'door', { at: 0.5 }),
    door('schoolroom', 'sewing', 'door', { at: 0.5 }),
    door('sewing', 'practice', 'door', { at: 0.5 }),
    door('landing', 'practice', 'door', { at: 0.5 }),
    door('landing', 'trunkroom', 'door', { at: 0.5 }),
    // 동쪽 날개
    door('ecorr2', 'ebalcony', 'door'),
    door('ecorr2', 'gallery2', 'double', { at: 0.5 }),
    door('ecorr2', 'study2', 'door'),
    door('ecorr2', 'guest4', 'door'),
    door('ecorr2', 'master', 'double', { at: 0.3 }),
    door('master', 'dressing', 'bolt', { id: 'dressing-bolt', label: '의상실 문', at: 0.5 }),
    door('ecorr2', 'thirdstair', 'locked', { id: 'third-door', key: 'key_third', label: '3층 계단 문', at: 0.2 }),
    door('ecorr2', 'servstair2', 'door', { at: 0.3 }),
    door('servstair2', 'servup2', 'boarded', { id: 'serv-boarded', label: '판자로 막힌 문', at: 0.5 }),
  ],
  stairs: [
    stairs('grand2', 'landing', 62, 59, 'down', 'N', 'ground', 'grand1', { w: 4, h: 3 }),
    stairs('back2', 'servstair2', 120, 55, 'down', 'N', 'ground', 'back1u'),
    stairs('third2', 'thirdstair', 110, 50, 'up', 'S', 'third', 'third3'),
    stairs('serv2u', 'servup2', 120, 60, 'up', 'S', 'third', 'serv3'),
  ],
};
